import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../shared/ui/components';
import { ScriptureText } from '../bible/ScriptureText';

/** Marca de salto de página en las notas (en su propio renglón). */
export const PAGE_BREAK = '— página —';
const BREAK_LINE = /^[ \t]*[-—–]+[ \t]*p[aá]gina[ \t]*[-—–]+[ \t]*$/gim;

export const splitPages = (text: string) =>
  text
    .split(BREAK_LINE)
    .map((p) => p.replace(/^\n+|\n+$/g, ''))
    .filter((p, i, all) => p.trim() !== '' || all.length === 1);

const SIZES = [0.95, 1.05, 1.2, 1.35, 1.55] as const;

function stored(key: string, fallback: number): number {
  try {
    const v = Number(localStorage.getItem(key));
    return Number.isFinite(v) && localStorage.getItem(key) !== null ? v : fallback;
  } catch {
    return fallback;
  }
}
function remember(key: string, value: number) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Sin almacenamiento (modo privado): solo no se recuerda.
  }
}

/**
 * Las notas de la clase como un libro: con saltos de página se leen una página a la vez (botones o
 * deslizando el dedo) y se recuerda la última página; A−/A+ ajustan la letra en este dispositivo.
 * Las citas bíblicas se abren al tocarlas. `page`/`onPage` permiten que otro (p. ej. el maestro en vivo) la controle.
 */
export function ClassReader({ text, readingKey, page: controlled, onPage, children }: { text: string; readingKey: string; page?: number; onPage?: (page: number) => void; children?: (pageText: string, page: number) => ReactNode }) {
  const pages = splitPages(text);
  const [own, setOwn] = useState(() => Math.min(stored(`keora:lectura:${readingKey}`, 0), pages.length - 1));
  const [size, setSize] = useState(() => Math.min(Math.max(stored('keora:lectura:letra', 1), 0), SIZES.length - 1));
  const page = Math.min(Math.max(controlled ?? own, 0), pages.length - 1);
  const touch = useRef<number | null>(null);
  const top = useRef<HTMLDivElement>(null);

  const go = (next: number) => {
    const p = Math.min(Math.max(next, 0), pages.length - 1);
    if (p === page) return;
    setOwn(p);
    remember(`keora:lectura:${readingKey}`, p);
    onPage?.(p);
    top.current?.scrollIntoView?.({ block: 'nearest' });
  };
  useEffect(() => remember('keora:lectura:letra', size), [size]);

  return (
    <div className="class-reader" ref={top}>
      <div className="class-reader__tools">
        <div role="group" aria-label="Tamaño de letra" className="class-reader__size">
          <Button variant="ghost" disabled={size === 0} onClick={() => setSize((s) => s - 1)} aria-label="Letra más pequeña">
            A−
          </Button>
          <Button variant="ghost" disabled={size === SIZES.length - 1} onClick={() => setSize((s) => s + 1)} aria-label="Letra más grande">
            A+
          </Button>
        </div>
        {pages.length > 1 ? (
          <span className="small muted">
            Página {page + 1} de {pages.length}
          </span>
        ) : null}
      </div>
      <div
        className="class-reader__page"
        style={{ fontSize: `${SIZES[size]}rem` }}
        onTouchStart={(e) => (touch.current = e.touches[0]?.clientX ?? null)}
        onTouchEnd={(e) => {
          const start = touch.current;
          const end = e.changedTouches[0]?.clientX;
          touch.current = null;
          if (start === null || end === undefined || Math.abs(end - start) < 60) return;
          go(end < start ? page + 1 : page - 1);
        }}
      >
        {children ? children(pages[page]!, page) : <ScriptureText text={pages[page]!} />}
      </div>
      {pages.length > 1 ? (
        <div className="class-reader__nav">
          <Button variant="secondary" disabled={page === 0} onClick={() => go(page - 1)}>
            ← Anterior
          </Button>
          <span className="small muted">
            {page + 1} / {pages.length}
          </span>
          <Button variant="secondary" disabled={page === pages.length - 1} onClick={() => go(page + 1)}>
            Siguiente →
          </Button>
        </div>
      ) : null}
    </div>
  );
}
